import React, {useState, useEffect} from 'react';
import Busca from "./Busca";
import Cadastrar from "./Cadastrar";
import {useAppSelector} from "lib/hooks";
import {ProprietarioObject} from "models/Proprietario";
import {CSSTransition, SwitchTransition} from "react-transition-group";
import {PendingProprietario} from "./controller";
import Confirmar from "./Confirmar";
import {ModalBodyPart} from "components/Modal";
import {useProprietarios} from "lib/DataHooks/proprietarios";
import {IonProgressBar} from "@ionic/react";
import {FazendaObject} from "models/Fazenda";

interface FormProprietarioEmbedProps {
  instance?: ProprietarioObject;
  proprietario?: PendingProprietario;
  doNotInsert?: boolean;
  state?: FormProprietarioPageState;
  fazenda?: FazendaObject;
  isActive?: boolean;

  // para poder retornar o cadastro quando doNotInsert = true
  onComplete: (instance: PendingProprietario) => void;
}

export type PreCadastro = Pick<ProprietarioObject, "nome" | "telefone">;
export type PosCadastro = Pick<ProprietarioObject, "pessoa_tipo" | "cidade" | "estado">;

export type FormProprietarioPageState = 'busca' | 'cadastrar' | 'confirmar';

const FormProprietarioEmbed: React.FC<FormProprietarioEmbedProps> = (props) => {
  if (props.instance && props.proprietario)
    throw new Error('instance e proprietario não podem ser definidos ao mesmo tempo');

  const {doNotInsert, onComplete, fazenda} = props;
  const isActive = props.isActive !== undefined ? props.isActive : true;
  const allowSelect = doNotInsert;
  const user = useAppSelector(state => state.app.user);
  const [instance, setInstance] = useState<ProprietarioObject|undefined>(props.instance);
  const [pageState, setPageState] = useState<FormProprietarioPageState>(props.state || 'busca');
  const [preCadastro, setPreCadastro] = useState<PreCadastro>();
  const [posCadastro, setPosCadastro] = useState<PosCadastro>();
  const [proprietario, setProprietario] = useState<PendingProprietario>();
  const [proprietarios, loadingProps] = useProprietarios({
    enabled: isActive,
  });

  // reset
  useEffect(() => {
    const instance = props.instance;
    if (instance) {
      setPreCadastro({nome: instance.nome, telefone: instance.telefone});
      setPosCadastro({pessoa_tipo: instance.pessoa_tipo, cidade: instance.cidade, estado: instance.estado});
      setInstance(instance);
    } else {
      setPreCadastro(undefined);
      setPosCadastro(undefined);
      setInstance(undefined);
    }
  }, [props.instance]);

  // reset via proprietario
  useEffect(() => {
    const proprietario = props.proprietario;
    if (proprietario) {
      // console.log('reset via proprietario', proprietario);
      setProprietario(proprietario);
      setInstance(proprietario.instance);
    }
  }, [props.proprietario]);


  const onCompleteCadastro = (proprietario: ProprietarioObject) => {
    const _proprietario: PendingProprietario = {
      mode: 'new',
      instance: proprietario,
    };
    setProprietario(_proprietario);

    onComplete(_proprietario);
  }

  const onConfirm = () => {
    if (!proprietario)
      throw new Error('Proprietario não definido');

    onComplete(proprietario);
  }

  const onSelect = (proprietario: ProprietarioObject) => {
    const _proprietario: PendingProprietario = {
      mode: 'use',
      instance: proprietario,
    };
    // console.log('onSelect', _proprietario);
    setProprietario(_proprietario);
    onComplete(_proprietario);
  }

  const onClickBackBusca = () => {
    if (doNotInsert) {
      setPageState('confirmar');
    } else {
      setPageState('cadastrar');
    }
  }

  const getContent = () => {
    if (pageState === 'busca')
      return (
        <Busca
          user={user}
          preCadastro={preCadastro}
          proprietarios={proprietarios}
          onSelect={allowSelect ? onSelect : undefined}
          onNotFound={(values) => {
            setPreCadastro(values);
            setPageState('cadastrar');
          }}
          onClickBack={instance ? onClickBackBusca : undefined}
        />
      );
    if (pageState === 'cadastrar' && preCadastro)
      return (
        <Cadastrar
          user={user}
          preCadastro={preCadastro}
          posCadastro={posCadastro}
          instance={instance}
          onClickBack={(cadastro) => {
            setPosCadastro(cadastro);
            setPageState('busca');
          }}
          onComplete={onCompleteCadastro}
          doNotInsert={doNotInsert}
        />
      )
    if (pageState === 'confirmar' && proprietario)
      return (
        <Confirmar
          proprietario={proprietario}
          onClickChange={() => setPageState('busca')}
          onConfirm={onConfirm}
          fazenda={fazenda}
        />
      );

    return null;
  }

  if (!pageState)
    return null;

  return (
    <ModalBodyPart>
      {loadingProps &&
        <IonProgressBar type="indeterminate"/>
      }
      {!loadingProps &&
        <SwitchTransition>
          <CSSTransition
            key={pageState}
            classNames="transition-slide-l"
            timeout={250}
          >
            <ModalBodyPart>
              {getContent()}
            </ModalBodyPart>
          </CSSTransition>
        </SwitchTransition>
      }
    </ModalBodyPart>
  );
}

export default FormProprietarioEmbed;
